import { type Chat, type ClientState, formatPhone, type PublicUser } from "@rhema/client";
import type { AvatarTone, DeliveryStatus } from "@rhema/ui";

// Turns client state into what the screens show. Kept free of React so it is easy to test.

const TONES: AvatarTone[] = ["blue", "sky", "sage", "rose", "sand", "moss", "stone"];

/** A stable avatar tone for an id, so a person keeps the same colour everywhere. */
export function toneFor(id: string): AvatarTone {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return TONES[h % TONES.length] ?? "stone";
}

export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

/** The other person in a direct chat. */
export function peerOf(chat: Chat, meId: string | undefined): PublicUser | undefined {
  if (chat.kind !== "direct") return undefined;
  return chat.members.find((m) => m.user.id !== meId)?.user ?? chat.members[0]?.user;
}

export function chatTitle(chat: Chat, meId: string | undefined): string {
  if (chat.kind === "group") return chat.title || "Unnamed group";
  if (chat.kind === "self") return "You";
  return peerOf(chat, meId)?.display_name || chat.title || "Someone";
}

export function isMuted(chat: Chat, now = Date.now()): boolean {
  return Boolean(chat.muted_until && new Date(chat.muted_until).getTime() > now);
}

/** Someone's status as one line, such as "📅 In a meeting", or "" when none is set or it ran out. */
export function statusLine(
  user: { status_emoji?: string; status_text?: string; status_until?: string | null } | undefined,
  now = Date.now(),
): string {
  if (!user) return "";
  if (user.status_until && new Date(user.status_until).getTime() <= now) return "";
  return [user.status_emoji, user.status_text].filter(Boolean).join(" ");
}

/**
 * Whether a chat shows as unread: it has new messages, or the person marked it unread. Marked
 * unread lasts until the chat is opened or read.
 */
export function isUnread(chat: Pick<Chat, "unread" | "marked_unread">): boolean {
  return chat.unread > 0 || chat.marked_unread === true;
}

/** What a chat adds to an unread total: its new messages, or one for a chat marked unread. */
export function unreadCount(chat: Pick<Chat, "unread" | "marked_unread">): number {
  return chat.unread > 0 ? chat.unread : chat.marked_unread === true ? 1 : 0;
}

export function isAdmin(chat: Chat): boolean {
  return chat.role === "owner" || chat.role === "admin";
}

export function canPost(chat: Chat): boolean {
  return chat.kind === "direct" || !chat.only_admins_post || isAdmin(chat);
}

function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function clockTime(iso: string): string {
  const d = new Date(iso);
  return `${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** Time beside a chat in the list: the clock today, then "Yesterday", the weekday, or the date. */
export function listTime(iso: string, now = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  if (sameDay(d, now)) return clockTime(iso);
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(d, yesterday)) return "Yesterday";
  const days = (now.getTime() - d.getTime()) / 86_400_000;
  if (days < 7) return d.toLocaleDateString("en-GB", { weekday: "long" });
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

/** Label for the divider between days in a conversation. */
export function dayLabel(iso: string, now = new Date()): string {
  const d = new Date(iso);
  if (sameDay(d, now)) return "Today";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(d, yesterday)) return "Yesterday";
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

export function dayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

export function presenceText(
  presence: ClientState["presence"][string] | undefined,
  now = new Date(),
): string | undefined {
  if (!presence) return undefined;
  if (presence.online) return "online";
  if (!presence.lastSeen) return undefined;
  const d = new Date(presence.lastSeen);
  if (sameDay(d, now)) return `last seen today at ${clockTime(presence.lastSeen)}`;
  return `last seen ${listTime(presence.lastSeen, now)}`;
}

/** Ticks for the last message in the list, when you sent it. */
export function lastStatus(chat: Chat, meId: string | undefined): DeliveryStatus | undefined {
  const last = chat.last_message;
  if (last?.kind !== "message" || last.sender_id !== meId || last.deleted) return undefined;
  const others = chat.members.filter((m) => m.user.id !== meId);
  if (!others.length) return "sent";
  if (others.every((m) => m.read_seq >= last.seq)) return "read";
  if (others.every((m) => m.delivered_seq >= last.seq)) return "delivered";
  return "sent";
}

export function memberNames(chat: Chat, meId: string | undefined): string {
  const names = chat.members.filter((m) => m.user.id !== meId).map((m) => firstName(m.user.display_name));
  const more = chat.member_count - chat.members.length;
  const list = [...names, ...(chat.members.some((m) => m.user.id === meId) ? ["you"] : [])].join(", ");
  return more > 0 ? `${list} and ${more} more` : list;
}

export type ChatView = {
  id: string;
  chat: Chat;
  kind: Chat["kind"];
  title: string;
  tone: AvatarTone;
  peer?: PublicUser;
  /** The photo to show: the other person's, the group's, or yours in your own chat. */
  photo: string | null;
  online: boolean;
  subtitle: string;
  time: string;
  preview: string;
  sender?: string;
  unread: number;
  /** Marked unread by the person, with no new messages: a dot in place of a count. */
  markedUnread: boolean;
  mention: boolean;
  muted: boolean;
  pinned: boolean;
  archived: boolean;
  typing: boolean;
  status?: DeliveryStatus;
};

export function chatView(chat: Chat, state: ClientState): ChatView {
  const meId = state.me?.id;
  const peer = peerOf(chat, meId);
  const presence = peer ? state.presence[peer.id] : undefined;
  const typing = (state.typing[chat.id] ?? []).filter((id) => id !== meId);
  const last = chat.last_message;
  let sender: string | undefined;
  if (chat.kind === "group" && last?.kind === "message" && last.sender_id && !last.deleted) {
    sender =
      last.sender_id === meId
        ? "You"
        : firstName(chat.members.find((m) => m.user.id === last.sender_id)?.user.display_name ?? "");
    if (!sender) sender = undefined;
  }
  return {
    id: chat.id,
    chat,
    kind: chat.kind,
    title: chatTitle(chat, meId),
    tone: chat.kind === "self" ? "self" : toneFor(peer?.id ?? chat.id),
    peer,
    photo:
      chat.kind === "self"
        ? (state.me?.avatar_url ?? null)
        : (peer?.avatar_url ??
          (chat.kind === "group" || chat.kind === "channel" ? chat.avatar_url : null) ??
          null),
    online: Boolean(presence?.online),
    subtitle:
      chat.kind === "group"
        ? memberNames(chat, meId)
        : chat.kind === "self"
          ? "Message yourself"
          : chat.kind === "channel"
            ? chat.about || `${chat.member_count} ${chat.member_count === 1 ? "member" : "members"}`
            : [
                presenceText(presence) ?? (peer?.phone ? formatPhone(peer.phone) : `@${peer?.handle ?? ""}`),
                statusLine(peer),
              ]
                .filter(Boolean)
                .join(" · "),
    time: last ? listTime(chat.last_message_at) : "",
    preview: state.previews[chat.id] ?? (last ? "" : "No messages yet"),
    sender,
    unread: chat.unread,
    markedUnread: chat.unread === 0 && chat.marked_unread === true,
    mention: chat.mentioned,
    muted: isMuted(chat),
    pinned: chat.pinned,
    archived: chat.archived,
    typing: typing.length > 0,
    status: lastStatus(chat, meId),
  };
}

export function initialsFor(title: string): string {
  const parts = title.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}
